import React, { useState, useEffect } from 'react';
import profile from '../../assets/profile.png';
import { getAuthContext } from '../../utils/jwt';
import { BASE_URL } from '../../utils/constants';
import { toast } from 'react-toastify';

interface Activity {
  id: string;
  subjectUserId: string;
  actorUserId: string;
  eventType: string;
  module: string;
  action: string;
  title: string;
  details: Record<string, any>;
  occurredAt: string;
}

interface ActivityAuditTableProps {
  memberId?: string; // Optional: if provided, fetch activities for specific member
  itemsPerPage?: number;
}

interface ApiResponse {
  content: Activity[];
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  last: boolean;
}

const ActivityAuditTable: React.FC<ActivityAuditTableProps> = ({
  memberId,
  itemsPerPage = 6,
}) => {
  const [logs, setLogs] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // Format date to readable string
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format action title to display
  const formatAction = (activity: Activity): string => {
    // If there's a title, use it
    if (activity.title) {
      return activity.title;
    }
    // Otherwise create a title from eventType
    return activity.eventType.replace(/\./g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get actor name from details or use default
  const getActorName = (activity: Activity): string => {
    // If there's a displayName in details, use it
    if (activity.details?.displayName) {
      return activity.details.displayName;
    }
    // Check if actor is current user
    const { userId } = getAuthContext();
    if (activity.actorUserId === userId) {
      return 'You';
    }
    return 'System';
  };

  // Get avatar for actor
  const getActorAvatar = (activity: Activity): string => {
    // Check if actor is current user
    const { userId } = getAuthContext();
    if (activity.actorUserId === userId) {
      // Return current user's avatar if available
      const savedProfileData = localStorage.getItem('profileData');
      if (savedProfileData) {
        const parsed = JSON.parse(savedProfileData);
        return parsed.profilePhoto || profile;
      }
    }
    return profile;
  };

  // Fetch activity logs from API
  const fetchActivityLogs = async (page: number = 0) => {
    try {
      setLoading(true);
      setError(null);

      const authContext = getAuthContext();
      const { token, tenantId, userId } = authContext;

      if (!token || !tenantId) {
        throw new Error('Authentication required');
      }

      let url: string;

      // If memberId is provided, fetch specific member's activity

      // Otherwise fetch tenant-wide activity
      url = `${BASE_URL}/v1/team/members/tenant/activity?size=${itemsPerPage}&page=${page}`;



      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch activity logs: ${response.status} - ${errorText}`);
      }

      const data: ApiResponse = await response.json();

      setLogs(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
      setCurrentPage(data.pageNumber);

    } catch (err) {
      // console.error('❌ Error fetching activity logs:', err);
      toast.error('Failed to fetch activity logs');
      setError(err instanceof Error ? err.message : 'Failed to fetch activity logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when page changes
  useEffect(() => {
    fetchActivityLogs(currentPage);
  }, [currentPage, memberId]);

  // Handle page change
  const handlePageChange = (page: number) => {
    if (page >= 0 && page < totalPages) {
      setCurrentPage(page);
    }
  };

  // Handle next page
  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Handle prev page
  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = (): number[] => {
    const pageNumbers: number[] = [];
    const maxVisiblePages = 10;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages are less than or equal to max visible
      for (let i = 0; i < totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Show pages with ellipsis logic
      if (currentPage < 5) {
        // Show first 10 pages
        for (let i = 0; i < maxVisiblePages; i++) {
          pageNumbers.push(i);
        }
      } else if (currentPage > totalPages - 5) {
        // Show last 10 pages
        for (let i = totalPages - maxVisiblePages; i < totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        // Show pages around current page
        const startPage = currentPage - 4;
        const endPage = currentPage + 5;
        for (let i = startPage; i < endPage && i < totalPages; i++) {
          pageNumbers.push(i);
        }
      }
    }

    return pageNumbers;
  };

  // Loading state
  if (loading) {
    return (
      <div className='bg-white rounded-md border border-gray-200 shadow-sm mt-6 p-6'>
        <h2 className='text-[20px] font-semibold text-[#3F5265] mb-4'>
          Activity & Audit Logs
        </h2>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00A4BD]" />
          <span className="ml-2 text-gray-500">Loading activity logs...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className='bg-white rounded-md border border-gray-200 shadow-sm mt-6 p-6'>
        <h2 className='text-[20px] font-semibold text-[#3F5265] mb-4'>
          Activity & Audit Logs
        </h2>
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          <p className="font-medium">Error loading activity logs</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={() => fetchActivityLogs(currentPage)}
            className="mt-2 text-sm bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='bg-white rounded-md border border-gray-200 shadow-sm mt-6 p-6'>
      <h2 className='text-[20px] font-semibold text-[#3F5265] mb-4'>
        Activity & Audit Logs
      </h2>

      {logs.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No activity logs found</p>
        </div>
      ) : (
        <>
          <table className='w-full text-left border border-gray-200'>
            <thead className='bg-gray-100 text-[#657584] text-[12px]'>
              <tr>
                <th className='px-6 py-3 font-normal'>ACTION</th>
                <th className='px-6 py-3 font-normal'>DATE</th>
                <th className='px-6 py-3 font-normal'>PERFORMED BY</th>
              </tr>
            </thead>
            <tbody className='text-[#3F5265] text-[14px]'>
              {logs.map((log) => (
                <tr
                  key={log.id}
                  className='border-b hover:bg-gray-50 transition-colors'
                >
                  <td className='px-6 py-3'>
                    <div className="font-medium">{formatAction(log)}</div>
                    <div className="text-xs text-gray-500">
                      {log.module} • {log.action}
                    </div>
                  </td>
                  <td className='px-6 py-3'>{formatDate(log.occurredAt)}</td>
                  <td className='px-6 py-3 flex items-center gap-2'>
                    <img
                      src={getActorAvatar(log)}
                      alt={getActorName(log)}
                      className='w-6 h-6 rounded-full'
                    />
                    {getActorName(log)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination Controls */}
          <div className='flex items-center justify-between mt-4 text-sm text-gray-500'>
            <span>
              Showing {logs.length} results out of {totalElements}
            </span>
            {totalPages > 1 && (
              <div className='flex items-center gap-2'>
                <button
                  disabled={currentPage === 0}
                  onClick={handlePrevPage}
                  className={`px-3 py-1 border rounded ${currentPage === 0
                    ? 'bg-gray-200 text-gray-300 cursor-not-allowed'
                    : 'bg-[#00A4BD] text-white hover:bg-[#00899E]'
                    }`}
                >
                  Prev
                </button>
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 border rounded ${currentPage === pageNum
                      ? 'bg-[#00A4BD] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                  >
                    {pageNum + 1}
                  </button>
                ))}
                <button
                  disabled={currentPage === totalPages - 1}
                  onClick={handleNextPage}
                  className={`px-3 py-1 border rounded ${currentPage === totalPages - 1
                    ? 'bg-gray-200 text-gray-300 cursor-not-allowed'
                    : 'bg-[#00A4BD] text-white hover:bg-[#00899E]'
                    }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ActivityAuditTable;